/**
 * Country calling codes for the checkout phone field (VAM-294).
 *
 * Built from the same ISO set and German names as the address country list
 * (countries.ts), intersected with the countries libphonenumber-js knows a calling
 * code for (territories such as AQ/BV have none). DE/AT/CH are pinned on top, the
 * rest is sorted alphabetically by German name.
 */

import { getCountryCallingCode, isSupportedCountry, type CountryCode } from 'libphonenumber-js/max';
import metadata from 'libphonenumber-js/max/metadata';
import { NATIONALITY_CODES, PINNED_NATIONALITY_CODES, getNationalityName } from './nationalities';

export interface PhoneCountryOption {
  /** ISO 3166-1 alpha-2, uppercase (libphonenumber's `CountryCode`). */
  code: CountryCode;
  /** German country name, e.g. 'Deutschland'. */
  name: string;
  /** Calling code without `+`, e.g. '49'. */
  callingCode: string;
  /** Shown above the separator, with a flag. */
  pinned: boolean;
}

/** Preselected country; fixed to Germany for the German market. */
export const DEFAULT_PHONE_COUNTRY: CountryCode = 'DE';

const collator = new Intl.Collator('de');

const toOption = (code: CountryCode, pinned: boolean): PhoneCountryOption => ({
  code,
  name: getNationalityName(code),
  callingCode: getCountryCallingCode(code),
  pinned,
});

const PINNED_CODES: CountryCode[] = PINNED_NATIONALITY_CODES.filter((code) =>
  isSupportedCountry(code)
);

/** Dropdown entries: DE/AT/CH (pinned, in that order), then all others A–Z. */
export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = [
  ...PINNED_CODES.map((code) => toOption(code, true)),
  ...NATIONALITY_CODES.filter(
    (code): code is CountryCode =>
      isSupportedCountry(code) && !PINNED_CODES.includes(code as CountryCode)
  )
    .map((code) => toOption(code, false))
    .sort((a, b) => collator.compare(a.name, b.name)),
];

/** Lookup by ISO code. */
export const PHONE_COUNTRY_BY_CODE = new Map<CountryCode, PhoneCountryOption>(
  PHONE_COUNTRY_OPTIONS.map((option) => [option.code, option])
);

/** Calling codes offered in the dropdown (prefix-free per E.164). */
export const CALLING_CODES = new Set(PHONE_COUNTRY_OPTIONS.map((option) => option.callingCode));

/**
 * Main country for a calling code shared by several countries (e.g. '1' → 'US',
 * '7' → 'RU', '44' → 'GB'), as listed first in the libphonenumber metadata.
 */
export function getMainCountryForCallingCode(callingCode: string): CountryCode | undefined {
  const candidates = metadata.country_calling_codes[callingCode] ?? [];
  return candidates.find((code) => PHONE_COUNTRY_BY_CODE.has(code as CountryCode)) as
    | CountryCode
    | undefined;
}
